import React from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Container,
  Typography,
  Grid,
  Box,
  Card,
  CardContent,
  CardMedia,
  Button,
  Chip,
  Rating,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Tab,
  Tabs,
  Avatar,
  Stack,
  IconButton,
  TextField,
  Fade,
  Zoom,
} from '@mui/material';
import {
  LocationOn,
  Bed,
  Bathtub,
  SquareFoot,
  Wifi,
  LocalParking,
  Pool,
  FitnessCenter,
  Security,
  AcUnit,
  Microwave,
  Kitchen,
  Check,
  Phone,
  Email,
  Share,
  Favorite,
  FavoriteBorder,
  ArrowBack,
  Star,
  Verified,
  Schedule,
} from '@mui/icons-material';

// Section border style - matching all other pages
const sectionBorderStyle = {
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  height: '4px',
  background: 'linear-gradient(90deg, #9b4d3c 0%, #d49b7b 50%, #e0e0e0 100%)',
  zIndex: 1,
  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
};

const PropertyDetailPage = () => {
  const { id } = useParams();
  const [tabValue, setTabValue] = React.useState(0);
  const [favorite, setFavorite] = React.useState(false);
  const [hoveredImage, setHoveredImage] = React.useState(null);
  const [mainImageError, setMainImageError] = React.useState(false);
  const [thumbnailErrors, setThumbnailErrors] = React.useState({});
  const [similarImageErrors, setSimilarImageErrors] = React.useState({});

  // Mock property data with reliable image URLs
  const property = {
    id: 1,
    title: 'Luxury Corporate Suite',
    type: 'Guest House',
    location: 'Mumbai, Pune',
    price: '$450',
    images: [
      'https://images.unsplash.com/photo-1598928506390-45a8fea8d101?w=800',
      'https://images.unsplash.com/photo-1582719478250-c89cae4dc85b?w=800',
      'https://images.unsplash.com/photo-1613490493576-7fde63acd811?w=800',
    ],
    rating: 4.9,
    reviews: 124,
    beds: 2,
    baths: 2,
    sqft: 1200,
    description: 'Experience luxury living in this stunning corporate suite located in the heart of downtown. Perfect for business travelers and executives, this property offers unparalleled comfort and convenience.',
    amenities: [
      'High-Speed WiFi',
      'Free Parking',
      'Swimming Pool',
      'Fitness Center',
      '24/7 Security',
      'Air Conditioning',
      'Fully Equipped Kitchen',
      'Smart TV',
      'Washer/Dryer',
      'Room Service',
    ],
    services: ['Housekeeping', 'Concierge', 'Airport Transfer'],
    host: {
      name: 'Marvelous Hospitality',
      avatar: 'https://randomuser.me/api/portraits/men/32.jpg',
      rating: 4.9,
      properties: 25,
    },
  };

  // Similar properties with unique, reliable images
  const similarProperties = [
    {
      id: 2,
      title: 'Executive Business Suite',
      location: 'Financial District, NY',
      price: '$525',
      rating: 4.8,
      image: 'https://images.unsplash.com/photo-1578683010236-d716f9a3f461?w=800',
    },
    {
      id: 3,
      title: 'Premium Studio Apartment',
      location: 'Midtown, New York',
      price: '$380',
      rating: 4.7,
      image: 'https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?w=800',
    },
    {
      id: 4,
      title: 'Luxury Penthouse Suite',
      location: 'Upper East Side, NY',
      price: '$750',
      rating: 4.9,
      image: 'https://images.unsplash.com/photo-1600607687920-4e2a09cf159d?w=800',
    },
  ];

  // Fallback images
  const fallbackMainImage = 'https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?w=800';
  const fallbackThumbnail = 'https://images.unsplash.com/photo-1560448204-603b3fc33ddc?w=800';
  const fallbackSimilarImage = 'https://images.unsplash.com/photo-1512917774080-9991f1c4c750?w=800';

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleMainImageError = () => {
    setMainImageError(true);
  };

  const handleThumbnailError = (index) => {
    setThumbnailErrors(prev => ({ ...prev, [index]: true }));
  };

  const handleSimilarImageError = (id) => {
    setSimilarImageErrors(prev => ({ ...prev, [id]: true }));
  };

  return (
    <Box sx={{ bgcolor: '#faf7f2', minHeight: '100vh' }}>
      {/* SECTION 1: HERO - No top border (like other pages) */}
      <Box
        sx={{
          position: 'relative',
          height: '70vh',
          minHeight: 600,
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            '&::after': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              background: 'linear-gradient(135deg, rgba(0,0,0,0.5) 0%, rgba(0,0,0,0.3) 50%, rgba(155,77,60,0.4) 100%)',
              zIndex: 1,
            }
          }}
        >
          <Box
            component="img"
            src={mainImageError ? fallbackMainImage : 'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=1600'}
            alt={property.title}
            onError={handleMainImageError}
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            }}
          />
        </Box>

        <Container
          maxWidth="xl"
          sx={{
            position: 'relative',
            zIndex: 3,
            height: '100%',
            display: 'flex',
            alignItems: 'flex-end',
            pb: 8,
          }}
        >
          <Fade in={true} timeout={1000}>
            <Box>
              <Link to="/hospitality" style={{ textDecoration: 'none' }}>
                <Button
                  startIcon={<ArrowBack />}
                  sx={{
                    color: 'white',
                    mb: 3,
                    '&:hover': {
                      bgcolor: 'rgba(255,255,255,0.1)',
                    },
                  }}
                >
                  Back to listings
                </Button>
              </Link>
              
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                <Chip
                  label={property.type}
                  sx={{
                    bgcolor: '#9b4d3c',
                    color: 'white',
                    fontWeight: 600,
                    fontSize: '0.9rem',
                    px: 1,
                  }}
                />
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <LocationOn sx={{ fontSize: 20, color: 'white', mr: 0.5 }} />
                  <Typography variant="body1" sx={{ color: 'white', fontWeight: 500 }}>
                    {property.location}
                  </Typography>
                </Box>
              </Box>

              <Typography
                variant="h1"
                sx={{
                  color: 'white',
                  fontWeight: 800,
                  fontSize: { xs: '2.5rem', md: '4rem' },
                  lineHeight: 1.2,
                  textShadow: '2px 2px 8px rgba(0,0,0,0.5)',
                  maxWidth: 800,
                }}
              >
                {property.title}
              </Typography>

              <Box sx={{ display: 'flex', alignItems: 'center', mt: 3, gap: 1 }}>
                <Rating value={property.rating} precision={0.1} readOnly sx={{ color: '#d49b7b' }} />
                <Typography variant="h6" sx={{ color: 'white', fontWeight: 600, ml: 1 }}>
                  {property.rating}
                </Typography>
                <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.8)', ml: 1 }}>
                  ({property.reviews} reviews)
                </Typography>
              </Box>
            </Box>
          </Fade>
        </Container>
      </Box>

      {/* SECTION 2: PROPERTY DETAILS - With gradient top border */}
      <Box sx={{ position: 'relative' }}>
        <Box sx={sectionBorderStyle} />
        <Container maxWidth="xl" sx={{ py: 6 }}>
          <Grid container spacing={4}>
            {/* Left Column - Main Content */}
            <Grid item xs={12} md={8}>
              {/* Image Gallery Thumbnails */}
              <Zoom in={true} timeout={800}>
                <Box sx={{ display: 'flex', gap: 2, mb: 4, flexWrap: 'wrap' }}>
                  {property.images.map((img, index) => (
                    <Box
                      key={index}
                      onMouseEnter={() => setHoveredImage(index)}
                      onMouseLeave={() => setHoveredImage(null)}
                      sx={{
                        width: 100,
                        height: 80,
                        borderRadius: 2,
                        overflow: 'hidden',
                        cursor: 'pointer',
                        border: hoveredImage === index ? '2px solid #9b4d3c' : '2px solid transparent',
                        transition: 'border-color 0.3s',
                      }}
                    >
                      <img
                        src={thumbnailErrors[index] ? fallbackThumbnail : img}
                        alt={`${property.title} ${index + 1}`}
                        onError={() => handleThumbnailError(index)}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    </Box>
                  ))}
                </Box>
              </Zoom>

              {/* Rating & Actions */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Rating value={property.rating} precision={0.1} readOnly sx={{ color: '#d49b7b' }} />
                  <Typography variant="h6" sx={{ ml: 1, fontWeight: 600, color: '#9b4d3c' }}>
                    {property.rating}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                    ({property.reviews} reviews)
                  </Typography>
                </Box>
                <Box>
                  <IconButton onClick={() => setFavorite(!favorite)} sx={{ color: '#9b4d3c' }}>
                    {favorite ? <Favorite /> : <FavoriteBorder />}
                  </IconButton>
                  <IconButton sx={{ color: '#9b4d3c' }}>
                    <Share />
                  </IconButton>
                </Box>
              </Box>

              {/* Key Features */}
              <Paper
                sx={{
                  p: 3,
                  mb: 4,
                  borderRadius: 3,
                  border: '2px solid #9b4d3c',
                  boxShadow: '0 5px 20px rgba(155,77,60,0.05)',
                }}
              >
                <Grid container spacing={3}>
                  <Grid item xs={4}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Bed sx={{ fontSize: 32, color: '#9b4d3c', mb: 1 }} />
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{property.beds} Beds</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={4}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Bathtub sx={{ fontSize: 32, color: '#9b4d3c', mb: 1 }} />
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{property.baths} Baths</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={4}>
                    <Box sx={{ textAlign: 'center' }}>
                      <SquareFoot sx={{ fontSize: 32, color: '#9b4d3c', mb: 1 }} />
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{property.sqft} sqft</Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Paper>

              {/* Tabs */}
              <Paper
                sx={{
                  borderRadius: 3,
                  border: '2px solid #d49b7b',
                  boxShadow: '0 5px 20px rgba(155,77,60,0.05)',
                  overflow: 'hidden',
                }}
              >
                <Tabs
                  value={tabValue}
                  onChange={handleTabChange}
                  sx={{
                    bgcolor: '#f8f5f0',
                    '& .MuiTab-root': { color: '#666' },
                    '& .Mui-selected': { color: '#9b4d3c !important', fontWeight: 600 },
                    '& .MuiTabs-indicator': { bgcolor: '#9b4d3c' },
                  }}
                >
                  <Tab label="Description" />
                  <Tab label="Amenities" />
                  <Tab label="Reviews" />
                </Tabs>
                <Box sx={{ p: 3 }}>
                  {tabValue === 0 && (
                    <Fade in={true} timeout={500}>
                      <Typography variant="body1" sx={{ lineHeight: 1.8, color: '#555' }}>
                        {property.description}
                      </Typography>
                    </Fade>
                  )}
                  {tabValue === 1 && (
                    <Fade in={true} timeout={500}>
                      <Grid container spacing={2}>
                        {property.amenities.map((amenity, index) => (
                          <Grid item xs={12} sm={6} key={index}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Check sx={{ color: '#9b4d3c', fontSize: 20 }} />
                              <Typography variant="body2" sx={{ color: '#555' }}>{amenity}</Typography>
                            </Box>
                          </Grid>
                        ))}
                      </Grid>
                    </Fade>
                  )}
                  {tabValue === 2 && (
                    <Fade in={true} timeout={500}>
                      <Box sx={{ textAlign: 'center', py: 4 }}>
                        <Star sx={{ fontSize: 60, color: '#d49b7b', opacity: 0.5, mb: 2 }} />
                        <Typography variant="h6" sx={{ color: '#333' }}>
                          Guest Reviews
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Reviews will be displayed here
                        </Typography>
                      </Box>
                    </Fade>
                  )}
                </Box>
              </Paper>

              {/* Host Info */}
              <Paper
                sx={{
                  p: 3,
                  mt: 4,
                  borderRadius: 3,
                  border: '2px solid #9b4d3c',
                  boxShadow: '0 5px 20px rgba(155,77,60,0.05)',
                }}
              >
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: '#333' }}>
                  Hosted by
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                  <Avatar src={property.host.avatar} sx={{ width: 70, height: 70, border: '2px solid #9b4d3c' }} />
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>{property.host.name}</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
                      <Rating value={property.host.rating} size="small" readOnly sx={{ color: '#d49b7b' }} />
                      <Typography variant="body2" color="text.secondary">
                        {property.host.properties} properties
                      </Typography>
                      <Chip
                        icon={<Verified sx={{ fontSize: 14 }} />}
                        label="Verified"
                        size="small"
                        sx={{ bgcolor: 'rgba(155,77,60,0.1)', color: '#9b4d3c' }}
                      />
                    </Box>
                  </Box>
                </Box>
              </Paper>
            </Grid>

            {/* Right Column - Booking Card */}
            <Grid item xs={12} md={4}>
              <Zoom in={true} timeout={1000}>
                <Paper
                  sx={{
                    p: 4,
                    borderRadius: 3,
                    border: '2px solid #9b4d3c',
                    boxShadow: '0 10px 30px rgba(155,77,60,0.1)',
                    position: 'sticky',
                    top: 100,
                  }}
                >
                  <Typography variant="h3" sx={{ color: '#9b4d3c', fontWeight: 700, mb: 1 }}>
                    {property.price}
                    <Typography component="span" variant="body1" sx={{ color: '#666', ml: 1 }}>
                      / night
                    </Typography>
                  </Typography>

                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <Schedule sx={{ fontSize: 16, color: '#9b4d3c', mr: 0.5 }} />
                    <Typography variant="body2" color="text.secondary">
                      Minimum stay: 1 night
                    </Typography>
                  </Box>

                  <Divider sx={{ my: 3, borderColor: '#e0e0e0' }} />

                  <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                    Select dates
                  </Typography>
                  <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        type="date"
                        label="Check-in"
                        InputLabelProps={{ shrink: true }}
                        size="small"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            '& fieldset': { borderColor: '#e0e0e0' },
                            '&:hover fieldset': { borderColor: '#9b4d3c' },
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        type="date"
                        label="Check-out"
                        InputLabelProps={{ shrink: true }}
                        size="small"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            '& fieldset': { borderColor: '#e0e0e0' },
                            '&:hover fieldset': { borderColor: '#9b4d3c' },
                          },
                        }}
                      />
                    </Grid>
                  </Grid>

                  <TextField
                    fullWidth
                    type="number"
                    label="Guests"
                    defaultValue={1}
                    InputProps={{ inputProps: { min: 1 } }}
                    size="small"
                    sx={{
                      mb: 4,
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': { borderColor: '#e0e0e0' },
                        '&:hover fieldset': { borderColor: '#9b4d3c' },
                      },
                    }}
                  />

                  <Button
                    fullWidth
                    variant="contained"
                    size="large"
                    sx={{
                      bgcolor: '#9b4d3c',
                      color: 'white',
                      py: 1.5,
                      fontSize: '1rem',
                      fontWeight: 600,
                      borderRadius: 2,
                      mb: 2,
                      '&:hover': { bgcolor: '#7a3b2e' },
                    }}
                  >
                    Check Availability
                  </Button>

                  <Button
                    fullWidth
                    variant="outlined"
                    size="large"
                    startIcon={<Phone />}
                    sx={{
                      borderColor: '#9b4d3c',
                      color: '#9b4d3c',
                      py: 1.5,
                      fontSize: '1rem',
                      fontWeight: 600,
                      borderRadius: 2,
                      '&:hover': {
                        borderColor: '#9b4d3c',
                        bgcolor: 'rgba(155,77,60,0.05)',
                      },
                    }}
                  >
                    Contact Host
                  </Button>

                  <Divider sx={{ my: 4, borderColor: '#e0e0e0' }} />

                  <List dense disablePadding>
                    <ListItem sx={{ px: 0 }}>
                      <ListItemText 
                        primary="Cleaning fee" 
                        secondary="$50" 
                        primaryTypographyProps={{ color: '#666' }}
                        secondaryTypographyProps={{ color: '#333', fontWeight: 600 }}
                      />
                    </ListItem>
                    <ListItem sx={{ px: 0 }}>
                      <ListItemText 
                        primary="Service fee" 
                        secondary="$30" 
                        primaryTypographyProps={{ color: '#666' }}
                        secondaryTypographyProps={{ color: '#333', fontWeight: 600 }}
                      />
                    </ListItem>
                    <ListItem sx={{ px: 0 }}>
                      <ListItemText 
                        primary="Total (for 1 night)" 
                        secondary="$530" 
                        primaryTypographyProps={{ color: '#333', fontWeight: 600 }}
                        secondaryTypographyProps={{ color: '#9b4d3c', fontWeight: 700, fontSize: '1.1rem' }}
                      />
                    </ListItem>
                  </List>

                  <Typography variant="caption" color="text.secondary" display="block" align="center" sx={{ mt: 2 }}>
                    You won't be charged yet
                  </Typography>
                </Paper>
              </Zoom>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* SECTION 3: SIMILAR PROPERTIES - With gradient top border */}
      <Box sx={{ position: 'relative' }}>
        <Box sx={sectionBorderStyle} />
        <Container maxWidth="xl" sx={{ py: 6 }}>
          <Typography
            variant="h3"
            sx={{
              fontWeight: 600,
              color: '#333',
              fontSize: { xs: '2rem', md: '2.5rem' },
              mb: 4,
            }}
          >
            Similar Properties
          </Typography>
          
          <Grid container spacing={4}>
            {similarProperties.map((item) => (
              <Grid item xs={12} sm={6} md={4} key={item.id}>
                <Zoom in={true} timeout={800}>
                  <Card
                    sx={{
                      borderRadius: 3,
                      border: '2px solid #9b4d3c',
                      transition: 'transform 0.3s, box-shadow 0.3s',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: '0 15px 30px rgba(155,77,60,0.15)',
                      },
                    }}
                  >
                    <CardMedia
                      component="img"
                      height="200"
                      image={similarImageErrors[item.id] ? fallbackSimilarImage : item.image}
                      alt={item.title}
                      onError={() => handleSimilarImageError(item.id)}
                      sx={{
                        transition: 'transform 0.5s',
                        '&:hover': {
                          transform: 'scale(1.05)',
                        },
                      }}
                    />
                    <CardContent>
                      <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                        {item.title}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <LocationOn sx={{ fontSize: 14, color: '#9b4d3c', mr: 0.5 }} />
                        <Typography variant="caption" color="text.secondary">
                          {item.location}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Rating value={item.rating} size="small" readOnly sx={{ color: '#d49b7b' }} />
                        <Typography variant="h6" sx={{ color: '#9b4d3c', fontWeight: 700 }}>
                          {item.price}
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Zoom>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>
    </Box>
  );
};

export default PropertyDetailPage;